import { Button, Col, Divider, Flex, Form, FormInstance, Row } from "antd";
import AddIcon from "../../../../../assets/icons/AddIcon";
import Empty from "../../../../../assets/icons/Empty";
import useModal from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import BankCard from "./_components/BankCard";
import ManageBankDrawer from "./_components/ManageBankDrawer";
import ManageMFSDrawer from "./_components/ManageMFSDrawer";
import MFSCard from "./_components/MFSCard";

export type ModalType = "ADD-BANK" | "ADD-MFS" | "UPDATE-BANK" | "UPDATE-MFS";
export type ModalData = { index: number };

const Payments = ({ form }: { form: FormInstance<any> }) => {
  const { open, handleOpen, handleClose } = useModal<ModalData, ModalType>();

  // ------ Watched values ------
  const paymentInfo = Form.useWatch("paymentInfo", form);
  const selectedPaymentInfo = paymentInfo?.[open.data?.index as number];

  const bankInfo = paymentInfo?.filter(
    (info: any) => info.payment_type === "BANK" && info?.bankAccount,
  );
  const mfsInfo = paymentInfo?.filter(
    (info: any) => info.payment_type === "MFS" && info?.mfsAccount,
  );

  // ------ Function for add/update bank information ------
  // const handleBankSave = (values: Record<string, unknown>) => {
  //   const index = open?.data?.index;
  //   const currentList = paymentInfo ?? [];
  //   let updatedList = [...currentList];
  //   console.log(currentList);

  //   if (typeof index === "number") {
  //     // Update existing bank info
  //     updatedList = currentList.map((item: any, i: number) =>
  //       i === index ? { ...item, ...values } : item,
  //     );
  //   } else {
  //     // Add new bank info
  //     updatedList = [...currentList, values];
  //   }

  //   form.setFieldsValue({ paymentInfo: updatedList });
  //   handleClose();
  // };
  // ------ Function for add/update bank information ------
  const handleBankSave = (values: Record<string, any>) => {
    const index = open?.data?.index;
    const currentList = paymentInfo ?? [];
    let updatedList = [...currentList];
    const { bankAccount, mfsAccount } = values || {};

    // Extract the account number from the values being added/updated
    const newAccountNumber =
      bankAccount?.account_number || mfsAccount?.account_number;

    if (typeof index !== "number") {
      // Adding new bank info
      const isDuplicate = currentList.some((item: any) => {
        // Bank account duplicate check
        if (bankAccount) {
          return item.bankAccount?.account_number === newAccountNumber;
        }

        // MFS account duplicate check: both account number AND mfs_type
        if (mfsAccount) {
          return (
            item.mfsAccount?.account_number === newAccountNumber &&
            item.mfsAccount?.mfs_type === mfsAccount.mfs_type
          );
        }

        return false;
      });

      if (isDuplicate) {
        toast("error", "Account number already exists");
        return;
      }

      updatedList = [...currentList, values];
    } else {
      // Updating existing bank info
      updatedList = currentList.map((item: any, i: number) =>
        i === index ? { ...item, ...values } : item,
      );

      const duplicates = updatedList.filter((item: any, i: number) => {
        if (i === index) return false; // skip current item

        // Bank account duplicate
        if (bankAccount) {
          return item.bankAccount?.account_number === newAccountNumber;
        }

        // MFS account duplicate
        if (mfsAccount) {
          return (
            item.mfsAccount?.account_number === newAccountNumber &&
            item.mfsAccount?.mfs_type === mfsAccount.mfs_type
          );
        }

        return false;
      });

      if (duplicates.length > 0) {
        toast("error", "Account number already exists");
        return;
      }
    }

    form.setFieldsValue({ paymentInfo: updatedList });
    handleClose();
  };

  return (
    <>
      <div className="space-y-6 p-5 rounded-xl border border-theme-indigo-50 mb-5">
        <Form
          className="custom-form"
          layout="vertical"
          // requiredMark="optional"
          size="large"
          form={form}
        >
          <Form.List name="paymentInfo">
            {(fields, { add, remove }) => {
              const banks = fields.filter(
                (field) => paymentInfo?.[field.name]?.payment_type === "BANK",
              );
              const mfs = fields.filter(
                (field) => paymentInfo?.[field.name]?.payment_type === "MFS",
              );

              return (
                <>
                  {/* ------ Bank Section ------ */}
                  <Flex justify="space-between" className="mb-6">
                    <h5 className="text-lg text-theme-gray-2 font-semibold">
                      Bank Account
                    </h5>
                    <Button
                      size="small"
                      onClick={() => {
                        add({ payment_type: "BANK" }); // <-- important
                        handleOpen({
                          type: "ADD-BANK",
                          data: { index: fields?.length || 0 },
                        });
                      }}
                      className="button-primary-light"
                    >
                      <AddIcon /> Add Bank
                    </Button>
                  </Flex>

                  {bankInfo?.length ? (
                    <Row gutter={[24, 24]}>
                      {banks.map((field) => (
                        <Col key={field.key} span={12}>
                          <BankCard
                            handleOpen={handleOpen}
                            field={field}
                            onRemove={() => remove(field.name)}
                            data={paymentInfo?.[field?.name]}
                            fields={fields}
                            form={form}
                          />
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <Flex justify="center" align="center">
                      <Empty />
                    </Flex>
                  )}

                  <Divider className="bg-theme-slate-200" />

                  {/* ------ MFS Section ------ */}
                  <Flex justify="space-between" className="mb-6">
                    <h5 className="text-lg text-theme-gray-2 font-semibold">
                      MFS Account
                    </h5>
                    <Button
                      size="small"
                      onClick={() => {
                        add({ payment_type: "MFS" }); // <-- important
                        handleOpen({
                          type: "ADD-MFS",
                          data: { index: fields?.length || 0 },
                        });
                      }}
                      className="button-primary-light"
                    >
                      <AddIcon /> Add MFS
                    </Button>
                  </Flex>

                  {mfsInfo?.length ? (
                    <Row gutter={[24, 24]}>
                      {mfs.map((field) => (
                        <Col key={field.key} span={6}>
                          <MFSCard
                            handleOpen={handleOpen}
                            field={field}
                            onRemove={() => remove(field.name)}
                            data={paymentInfo?.[field?.name]}
                            fields={fields}
                            form={form}
                          />
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <Flex justify="center" align="center">
                      <Empty />
                    </Flex>
                  )}

                  {/* Drawers */}
                  <ManageMFSDrawer
                    open={
                      open.open &&
                      (open.type === "UPDATE-MFS" || open.type === "ADD-MFS")
                    }
                    data={open.data as ModalData}
                    type={open.type as ModalType}
                    onFinish={handleBankSave}
                    remove={remove}
                    handleClose={handleClose}
                    defaultValues={selectedPaymentInfo}
                  />

                  <ManageBankDrawer
                    open={
                      open.open &&
                      (open.type === "UPDATE-BANK" || open.type === "ADD-BANK")
                    }
                    data={open.data as ModalData}
                    type={open.type as ModalType}
                    onFinish={handleBankSave}
                    remove={remove}
                    handleClose={handleClose}
                    defaultValues={selectedPaymentInfo}
                  />
                </>
              );
            }}
          </Form.List>
        </Form>
      </div>
    </>
  );
};

export default Payments;
